
import { supabase } from '@/integrations/supabase/client';
import { searchDocumentsSemanticly, SemanticSearchOptions, SemanticSearchResult } from './semantic-search-service';

export interface DocumentSearchResult {
  results: SemanticSearchResult[];
  searchType: 'semantic' | 'database_fallback' | 'text_search';
  success: boolean;
  source: string;
  queryProcessed: string;
  debugInfo?: {
    threshold: number;
    attemptedStrategies: string[];
    foundDocumentsCount: number;
    searchTerm: string;
  };
}

/**
 * Enhanced integrated document search with multiple fallback strategies
 */
export const searchDocumentsWithFallback = async (
  query: string, 
  categories?: string[]
): Promise<DocumentSearchResult> => {
  console.log('🔍 [SEMANTIC-DEBUG] Starting enhanced document search for:', query);
  
  const debugInfo = {
    threshold: 0.3, // LOWERED from 0.5 to 0.3 for better recall
    attemptedStrategies: [] as string[],
    foundDocumentsCount: 0,
    searchTerm: query
  };

  // Enhanced: Multiple threshold attempts with progressive lowering
  const thresholds = [0.3, 0.2, 0.15, 0.1]; // Even more aggressive threshold lowering
  
  for (const threshold of thresholds) {
    console.log(`🔍 [SEMANTIC-DEBUG] Attempting semantic search with threshold: ${threshold}`);
    debugInfo.threshold = threshold;
    debugInfo.attemptedStrategies.push(`semantic-threshold-${threshold}`);
    
    try {
      console.log('📡 [SEMANTIC-DEBUG] Attempting semantic search via edge function...');
      const semanticResult = await searchDocumentsSemanticly({
        query,
        categories,
        matchThreshold: threshold,
        maxResults: 10
      });

      if (semanticResult.success && semanticResult.results.length > 0) {
        debugInfo.foundDocumentsCount = semanticResult.results.length;
        console.log(`✅ [SEMANTIC-DEBUG] Semantic search successful! Found ${semanticResult.results.length} results with threshold ${threshold}`);
        console.log(`📊 [SEMANTIC-DEBUG] Results:`, semanticResult.results.map(r => ({
          title: r.title,
          similarity: r.similarity,
          category: r.category,
          contentLength: r.content.length
        })));
        
        return {
          results: semanticResult.results,
          searchType: semanticResult.searchType === 'text_fallback' ? 'text_search' : semanticResult.searchType,
          success: true,
          source: 'edge_function',
          queryProcessed: query,
          debugInfo
        };
      } else {
        console.log(`❌ [SEMANTIC-DEBUG] No results with threshold ${threshold}, trying lower threshold...`);
      }
    } catch (error) {
      console.warn(`⚠️ [SEMANTIC-DEBUG] Semantic search edge function failed with threshold ${threshold}:`, error);
    }
  }

  // Enhanced fallback: Direct database search with chunk-based approach
  console.log('📝 [SEMANTIC-DEBUG] All semantic thresholds failed, falling back to enhanced database search...');
  debugInfo.attemptedStrategies.push('enhanced-database-search');
  
  try {
    // Check if we have document chunks with embeddings
    console.log('🔍 [SEMANTIC-DEBUG] Checking document chunks availability...');
    
    const { data: chunksData, error: chunksError } = await supabase
      .from('document_chunks')
      .select(`
        id,
        content,
        document_id,
        knowledge_documents!inner(
          id,
          title,
          category,
          is_active
        )
      `)
      .eq('knowledge_documents.is_active', true)
      .limit(5);

    if (!chunksError && chunksData && chunksData.length > 0) {
      console.log(`🔍 [SEMANTIC-DEBUG] Found ${chunksData.length} document chunks, attempting chunk-based search...`);
      
      // Try chunk-based text search
      const expandedTerms = expandSearchTerms(query);
      console.log(`🔍 [SEMANTIC-DEBUG] Expanded search terms for chunks:`, expandedTerms);
      
      for (const searchTerm of expandedTerms) {
        const { data: searchResults, error: searchError } = await supabase
          .from('document_chunks')
          .select(`
            id,
            content,
            document_id,
            knowledge_documents!inner(
              id,
              title,
              category,
              is_active
            )
          `)
          .eq('knowledge_documents.is_active', true)
          .textSearch('content', searchTerm)
          .limit(10);

        if (!searchError && searchResults && searchResults.length > 0) {
          debugInfo.foundDocumentsCount = searchResults.length;
          console.log(`✅ [SEMANTIC-DEBUG] Chunk-based search successful! Found ${searchResults.length} chunks`);
          
          // Transform chunk results to semantic search format
          const results: SemanticSearchResult[] = searchResults.map(chunk => ({
            chunkId: chunk.id,
            documentId: chunk.document_id,
            title: chunk.knowledge_documents.title,
            content: chunk.content,
            category: chunk.knowledge_documents.category,
            similarity: 0.7
          }));

          return {
            results,
            searchType: 'text_search',
            success: true,
            source: 'chunk_based_search',
            queryProcessed: searchTerm,
            debugInfo
          };
        }
      }
    }

    // Final fallback: Document-level search
    console.log('📝 [SEMANTIC-DEBUG] Chunk search failed, trying document-level search...');
    debugInfo.attemptedStrategies.push('document-level-search');
    
    const expandedTerms = expandSearchTerms(query);
    console.log(`🔍 [SEMANTIC-DEBUG] Expanded search terms for documents:`, expandedTerms);
    
    for (const searchTerm of expandedTerms) {
      console.log(`📝 [SEMANTIC-DEBUG] Trying document search with term: "${searchTerm}"`);
      
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select(`
          id,
          title,
          content,
          category,
          created_at,
          chunk_count
        `)
        .eq('is_active', true)
        .textSearch('content', searchTerm)
        .limit(10);

      if (!error && data && data.length > 0) {
        debugInfo.foundDocumentsCount = data.length;
        console.log(`✅ [SEMANTIC-DEBUG] Document search successful! Found ${data.length} documents`);
        console.log(`📊 [SEMANTIC-DEBUG] Found documents:`, data.map(d => ({
          title: d.title,
          category: d.category,
          contentLength: d.content?.length || 0,
          chunkCount: d.chunk_count
        })));
        
        const results: SemanticSearchResult[] = data.map(doc => ({
          chunkId: doc.id,
          documentId: doc.id,
          title: doc.title,
          content: doc.content,
          category: doc.category,
          similarity: 0.6
        }));

        return {
          results,
          searchType: 'text_search',
          success: true,
          source: 'document_fallback',
          queryProcessed: searchTerm,
          debugInfo
        };
      }
    }

  } catch (error) {
    console.error('❌ [SEMANTIC-DEBUG] All search strategies failed:', error);
  }
  
  console.log('❌ [SEMANTIC-DEBUG] No documents found with any strategy');
  return {
    results: [],
    searchType: 'text_search',
    success: false,
    source: 'failed',
    queryProcessed: query,
    debugInfo
  };
};

/**
 * Enhanced: Expand search terms for better matching
 */
function expandSearchTerms(query: string): string[] {
  const lowerQuery = query.toLowerCase();
  const terms = [query]; // Always include original query
  
  // NAV-specific expansions
  if (lowerQuery.includes('nav')) {
    terms.push(
      'omverdenanalyse',
      'arbeidsmarkedsanalyse',
      'nav rapport',
      'arbeidsmarked',
      'arbeidskraft',
      'sysselsetting',
      'ledighet',
      'prognose',
      'fremtid'
    );
  }
  
  // Market analysis terms
  if (lowerQuery.includes('arbeidsmarked') || lowerQuery.includes('arbeidskraft')) {
    terms.push(
      'omverdenanalyse',
      'arbeidsmarkedsanalyse',
      'etterspørsel',
      'kompetanse',
      'fremtid',
      'prognose',
      'utvikling',
      'bransjer',
      'sektor'
    );
  }
  
  // Future/development terms
  if (lowerQuery.includes('fremover') || lowerQuery.includes('utvikle') || lowerQuery.includes('framtid')) {
    terms.push(
      'prognose',
      'fremtidsutsikter',
      'utvikling',
      'trend',
      'perspektiv',
      'behovsprognose'
    );
  }
  
  // Industry terms
  if (lowerQuery.includes('bransje') || lowerQuery.includes('sektor')) {
    terms.push(
      'helse',
      'teknologi',
      'bygg',
      'utdanning',
      'omsorg',
      'it',
      'digital',
      'grønn',
      'miljø',
      'service',
      'industri'
    );
  }
  
  return [...new Set(terms)]; // Remove duplicates
}

/**
 * Enhanced: Check if a query should trigger document search
 */
export const shouldSearchDocuments = (query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  
  console.log(`[Document-Search] 🔍 ENHANCED CHECK: "${query}"`);
  
  // Enhanced: More flexible NAV detection
  const navTerms = [
    'nav omverdenanalyse', 'navs omverdenanalyse', 'nav-omverdenanalyse',
    'nav rapport', 'navs rapport', 'nav-rapport',
    'nav arbeidsmarkedsanalyse', 'navs arbeidsmarkedsanalyse',
    'omverdenanalyse', 'arbeidsmarkedsanalyse',
    'hva sier nav', 'ifølge nav', 'nav sier', 'i nav-rapporten',
    'kan du finne i nav', 'søk i nav dokument', 'finn i nav rapport',
    'navs rapporter', 'nav rapporter', 'nav sin rapport',
    'arbeidsmarked', 'arbeidskraft', 'etterspørsel etter arbeidskraft',
    // Enhanced: More terms for document search
    'kunnskapsgrunnlag', 'søk ditt kunnskapsgrunnlag', 'finn i dokumenter',
    'rapporten sier', 'dokumenter', 'analyser'
  ];
  
  const hasNavTerms = navTerms.some(term => lowerQuery.includes(term));
  
  if (hasNavTerms) {
    console.log(`[Document-Search] ✅ DOCUMENT MENTION DETECTED: Document search approved`);
    return true;
  }
  
  console.log(`[Document-Search] ❌ NO DOCUMENT MENTION: Document search skipped`);
  return false;
};

/**
 * Enhanced: Format search results with better context
 */
export const formatDocumentSearchResults = (searchResult: DocumentSearchResult): string => {
  if (!searchResult.success || searchResult.results.length === 0) {
    let message = 'Jeg fant ingen relevante dokumenter for spørsmålet ditt.';
    
    if (searchResult.debugInfo) {
      message += `\n\n[DEBUG INFO: Søkte med ${searchResult.debugInfo.attemptedStrategies.length} strategier, laveste threshold: ${searchResult.debugInfo.threshold}]`;
    }
    
    return message;
  }

  const { results, searchType, source, debugInfo } = searchResult;
  
  // Enhanced: Better structured context for OpenAI
  let formatted = `--- DOKUMENTKONTEKST FRA NAV/OMVERDENANALYSER ---\n\n`;
  
  // Add metadata about search quality
  const searchMethod = searchType === 'semantic' ? 'semantisk søk' : 'tekstsøk';
  const sourceInfo = source === 'edge_function' ? 'edge function' : 
                    source === 'chunk_based_search' ? 'chunk-basert søk' :
                    source === 'document_fallback' ? 'dokument-fallback' : 'tekst-fallback';
  
  formatted += `Søkemetode: ${searchMethod} via ${sourceInfo}\n`;
  formatted += `Antall dokumenter funnet: ${results.length}\n`;
  
  if (debugInfo) {
    formatted += `Threshold brukt: ${debugInfo.threshold}\n`;
    formatted += `Strategier forsøkt: ${debugInfo.attemptedStrategies.join(', ')}\n`;
  }
  
  formatted += `\n`;
  
  results.forEach((result, index) => {
    formatted += `### DOKUMENT ${index + 1}: ${result.title}\n`;
    formatted += `**Kategori:** ${result.category}\n`;
    
    if (searchType === 'semantic') {
      formatted += `**Relevans:** ${(result.similarity * 100).toFixed(1)}%\n`;
    }
    
    formatted += `\n**INNHOLD:**\n${result.content}\n\n`;
    
    if (index < results.length - 1) {
      formatted += `---\n\n`;
    }
  });

  formatted += `\n--- SLUTT DOKUMENTKONTEKST ---\n\n`;
  formatted += `KRITISK INSTRUKSJON: Analyser og syntétiser informasjonen ovenfor om NAVs analyser av fremtidens arbeidsmarked. OPPGI ALLTID konkrete kildehenvisninger til dokumenttitlene når du svarer. Fokuser spesielt på hvilke bransjer som forventes å trenge arbeidskraft i fremtiden basert på NAVs prognoser og analyser.`;

  return formatted;
};
